'use client';

import { Calendar as CalendarIcon } from 'lucide-react';
import { Avatar, Link, Skeleton, StatusIcon } from '@mokoboko/components';

import { getOneTimeEmptyState } from '@/helpers/class';
import {
  describeBookingClose,
  getServedBookingRules,
} from '@/helpers/class-rules';
import { ChevronDown } from '@/icons';
import {
  formatDateInTimeZone,
  formatDayKey,
  getFullName,
  getUserTimezone,
  mergeClassNames,
} from '@/lib/utils';
import type { Class } from '@/state/class-groups';
import { SubstituteBadge } from './substitute-badge';
import { ClassCanceledState, SeeAllClassesState } from './class-canceled-state';
import { EmptyCalendarState } from './empty-calendar-state';
import { useBookingContext } from './calendar/calendar-context';
import { isSlotFull, visibleSpots } from './calendar/calendar-utils';
import type { CalendarSlot } from './calendar';
import type { BookingContextValue } from './calendar/calendar.types';

const Row = ({
  media,
  title,
  subtitle,
  badge,
  className,
  textGap = 'gap-0.5',
}: {
  media: React.ReactNode;
  title: React.ReactNode;
  subtitle: string | null;
  badge?: React.ReactNode;
  className?: string;
  // A weekday title always ends in descenders that eat into the space under
  // it, while an instructor name usually has none. Equal gaps therefore read
  // unequal, and the rows sit side by side where that shows.
  textGap?: string;
}) => (
  <div className={mergeClassNames('flex items-center gap-3', className)}>
    <div className="shrink-0">{media}</div>
    <div className={mergeClassNames('flex min-w-0 flex-col', textGap)}>
      <div className="flex items-center gap-2">
        <p className="truncate text-sm font-semibold leading-5 text-gray-900">
          {title}
        </p>
        {badge}
      </div>
      {subtitle && (
        <p className="truncate text-sm leading-5 text-gray-600">{subtitle}</p>
      )}
    </div>
  </div>
);

// Both the StatusIcon (lg) and the Avatar (md) are 40px.
const RowSkeleton = ({
  className,
  textGap = 'gap-0.5',
}: {
  className?: string;
  textGap?: string;
}) => (
  <div className={mergeClassNames('flex items-center gap-3', className)}>
    <div className="shrink-0">
      <Skeleton isCircle height={40} />
    </div>
    <div className={mergeClassNames('flex min-w-0 flex-col', textGap)}>
      <Skeleton text="body-sm" width={150} />
      <Skeleton text="body-sm" width={110} />
    </div>
  </div>
);

// Calendar.Root's own wrapper comes first: the panel renders inside it.
export const OneTimeBookingSkeleton = ({
  showInstructor,
}: {
  showInstructor: boolean;
}) => (
  <div className="flex flex-wrap gap-4 md:gap-8">
    <div className="flex w-full flex-col gap-5 sm:gap-6">
      <div className="flex flex-col gap-1">
        <Skeleton text="body-lg" width={140} />
        <Skeleton text="body-sm" width={100} />
      </div>
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-10">
        <RowSkeleton textGap="gap-1.5" />
        {showInstructor && <RowSkeleton className="gap-4" />}
      </div>
    </div>
  </div>
);

type OneTimeInstructor = {
  id?: string;
  name: string;
  avatar?: string;
  isSubstitute?: boolean;
};

type OneTimeDetails = {
  start: string;
  end: string | null;
  timeZone: string;
  instructor: OneTimeInstructor | null;
};

// With no bookable slot, the class read still says when the class runs and who
// teaches it, a substitute included, read in the zone the availability request
// sends.
const oneTimeDetailsOf = (
  classData: Class,
  slot: CalendarSlot | null
): OneTimeDetails | null => {
  if (slot) {
    return {
      start: slot.startTime,
      end: slot.endTime,
      timeZone: slot.timezone,
      instructor: slot.instructorName
        ? {
            id: slot.instructorId,
            name: slot.instructorName,
            avatar: slot.instructorAvatar,
            isSubstitute: slot.isSubstitute,
          }
        : null,
    };
  }
  const start = classData.next_occurrence_start;
  if (!start) return null;
  const teacher = classData.next_occurrence_instructor;
  return {
    start,
    end: classData.next_occurrence_end ?? null,
    timeZone: getUserTimezone(),
    instructor: teacher
      ? {
          id: teacher.admin_user_id,
          name: getFullName(teacher.first_name, teacher.last_name),
          avatar: teacher.profile_picture ?? undefined,
          isSubstitute: teacher.is_substituted,
        }
      : null,
  };
};

// Sold out is a fact about the class and a studio publishing its spot counts
// is a choice, so the two answer to different settings, as in the slot list.
const spotsLineOf = (
  slot: CalendarSlot,
  booking: BookingContextValue
): string | null => {
  if (booking.isSlotBooked(slot)) return 'You are booked';
  if (isSlotFull(slot)) return booking.showFullyBooked ? 'Fully booked' : null;
  return visibleSpots(slot, booking)?.text ?? null;
};

// No bookable slot: the class's own start and booking rules say why.
const OneTimeUnavailableState = ({
  classData,
  start,
  end,
  timeZone,
}: {
  classData: Class;
  start: string;
  end: string | null;
  timeZone: string;
}) => {
  const { minNoticeHours, maxDaysInAdvance } = getServedBookingRules(classData);
  const occurrenceState = getOneTimeEmptyState({
    start,
    end,
    timeZone,
    maxDaysInAdvance,
  });

  if (occurrenceState.kind === 'not-open') {
    return (
      <EmptyCalendarState
        title="Booking is not open yet"
        description={`Booking opens on ${formatDayKey(occurrenceState.opensOn)}.`}
      />
    );
  }

  if (occurrenceState.kind === 'closed') {
    return (
      <SeeAllClassesState
        title="Booking has closed"
        description={describeBookingClose(minNoticeHours)}
      />
    );
  }

  if (occurrenceState.kind === 'started') {
    return (
      <SeeAllClassesState
        title="This class has already started"
        description="You can book another class instead."
      />
    );
  }

  return (
    <SeeAllClassesState
      title="This class has already happened"
      description="It ran one time only and is not planned again."
    />
  );
};

const OneTimeInstructorRow = ({
  instructor,
  onClick,
}: {
  instructor: OneTimeInstructor;
  onClick?: () => void;
}) => (
  <Row
    media={<Avatar name={instructor.name} src={instructor.avatar} size="md" />}
    className="gap-4"
    title={
      onClick ? (
        <Link
          color="gray"
          size="sm"
          className="capitalize"
          onClick={onClick}
          iconRight={<ChevronDown rotate={-90} className="h-4 w-4 shrink-0" />}
        >
          {instructor.name}
        </Link>
      ) : (
        <span className="capitalize">{instructor.name}</span>
      )
    }
    subtitle="Instructor"
    badge={instructor.isSubstitute ? <SubstituteBadge /> : undefined}
  />
);

/**
 * The booking body for a one-time class: the single date and the single
 * instructor, in place of the month grid and the slot picker. Lives inside
 * `Calendar.Root`, so it reads the same booking state every other surface does
 * and the slot it shows is the one checkout receives. The details show with or
 * without a bookable slot; without one, a notice under them says why.
 */
export const OneTimeBooking = ({
  classData,
  slot,
  showDuration,
  onInstructorClick,
}: {
  classData: Class;
  slot: CalendarSlot | null;
  showDuration: boolean;
  onInstructorClick?: (adminUserId: string) => void;
}) => {
  const booking = useBookingContext();
  const details = oneTimeDetailsOf(classData, slot);

  // No date means its date was taken away, so nothing is left to book.
  if (!details) return <ClassCanceledState />;
  if (!booking) return null;

  const spots = slot ? spotsLineOf(slot, booking) : null;
  const startTime = booking.formatSlotTime(details.start, details.timeZone);
  // The end gives the class's length away, which the studio can hide.
  const time =
    details.end && showDuration
      ? `${startTime} – ${booking.formatSlotTime(details.end, details.timeZone)}`
      : startTime;
  const instructorId = details.instructor?.id;

  return (
    <div className="flex w-full flex-col gap-5 sm:gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="heading-section">Class details</h2>
        <p className="text-sm text-gray-700">One date only</p>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-10">
        <Row
          media={<StatusIcon variant="filled" size="lg" icon={CalendarIcon} />}
          // From the instant, like the time beside it and checkout: the
          // studio's day can be another day where the customer is.
          title={formatDateInTimeZone(
            new Date(details.start),
            details.timeZone,
            'long'
          )}
          subtitle={spots ? `${time} · ${spots}` : time}
          textGap="gap-1.5"
        />

        {booking.showInstructor && details.instructor && (
          <OneTimeInstructorRow
            instructor={details.instructor}
            onClick={
              onInstructorClick && instructorId
                ? () => onInstructorClick(instructorId)
                : undefined
            }
          />
        )}
      </div>

      {!slot && (
        <OneTimeUnavailableState
          classData={classData}
          start={details.start}
          end={details.end}
          timeZone={details.timeZone}
        />
      )}
    </div>
  );
};
